import { filterQuery } from './historyApi';
import { getJson, patchJson, postJson, postQuery } from './restClient';
import type {
  AnalysisHistoryFilters,
  BacktestCombo,
  BacktestRollup,
  BacktestRunScope,
  BacktestRunState,
  BacktestSettings,
  BacktestSettingsPatch,
  BacktestStoredResponse,
} from 'types/history';

/**
 * The rule set a request addresses. Results are stored per rule set, so every
 * read has to say which one it wants — omitting them lets the server fall back
 * to the saved settings.
 */
export interface BacktestRuleSetParams {
  window?: number;
  /** Time stop in candles; 0 = disabled. */
  closeWindow?: number;
  /** Stop-loss distance multiplier; 1 = the stop each analysis proposed. */
  stopMult?: number;
  /** Which replay engine's results to read; omit for the current one. */
  replayVersion?: number;
}

export interface ListBacktestsParams extends AnalysisHistoryFilters, BacktestRuleSetParams {
  limit?: number;
  offset?: number;
}

function ruleParams(qs: URLSearchParams, rules: BacktestRuleSetParams): void {
  if (rules.window != null) qs.set('window', String(rules.window));
  // 0 is meaningful (time stop off), so compare against null rather than falsy.
  if (rules.closeWindow != null) qs.set('close_window', String(rules.closeWindow));
  if (rules.stopMult != null) qs.set('stop_mult', String(rules.stopMult));
  if (rules.replayVersion != null) qs.set('replay_version', String(rules.replayVersion));
}

/**
 * Read persisted backtests for a window pair: a page of per-analysis rollups plus
 * the aggregate over every matching row. Empty until a sweep has run for that
 * pair.
 *
 * Takes the History filters and serialises them through the *same* `filterQuery`
 * the table and the Excel export use — that is what keeps the summary bar
 * describing the set on screen rather than the whole database.
 */
export function listStoredBacktests(
  params: ListBacktestsParams = {},
  signal?: AbortSignal,
): Promise<BacktestStoredResponse> {
  const qs = filterQuery(params);
  // Always sent, not only when a verdict/grade filter is set: the rule set is
  // what selects the sweep being read, so it is never optional here.
  ruleParams(qs, params);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return getJson<BacktestStoredResponse>(`/analyses/backtest/stored${suffix}`, signal);
}

/** Stored verdicts for a specific set of analyses (the visible History page). */
export function getStoredBacktestsByIds(
  window: number,
  closeWindow: number,
  stopMult: number,
  replayVersion: number,
  ids: string[],
  signal?: AbortSignal,
): Promise<BacktestRollup[]> {
  return postJson<BacktestRollup[]>(
    '/analyses/backtest/stored/by-ids',
    { window, close_window: closeWindow, stop_mult: stopMult, replay_version: replayVersion, ids },
    signal,
  );
}

/**
 * Start (or report) the sweep for a rule set.
 *
 * `missing` recomputes only analyses without a settled verdict — never
 * backtested, or still OPEN/PENDING/no-data — and deletes nothing. `all` clears
 * the rule set and rebuilds it, so an aborted run leaves a partial set; that is
 * the one worth confirming before starting.
 *
 * Non-destructive across rule sets either way: results stored under other rules
 * always survive — which is what lets a 2x stop sweep sit alongside the 1x
 * baseline it is meant to be compared against.
 */
export function runFullBacktest(
  window: number,
  closeWindow: number,
  stopMult: number,
  scope: BacktestRunScope = 'missing',
): Promise<BacktestRunState> {
  return postQuery<BacktestRunState>('/analyses/backtest/run', {
    window,
    close_window: closeWindow,
    stop_mult: stopMult,
    scope,
  });
}

/** Poll the current/last sweep progress. */
export function getBacktestRunStatus(signal?: AbortSignal): Promise<BacktestRunState> {
  return getJson<BacktestRunState>('/analyses/backtest/run', signal);
}

/** Backtest one analysis, including the forward candle window for charting. */
export function getAnalysisBacktest(
  id: string,
  rules: BacktestRuleSetParams,
  signal?: AbortSignal,
): Promise<BacktestRollup> {
  const qs = new URLSearchParams();
  ruleParams(qs, rules);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return getJson<BacktestRollup>(`/analyses/${id}/backtest${suffix}`, signal);
}

/** The persisted replay rules (created with today's defaults on first read). */
export function getBacktestSettings(signal?: AbortSignal): Promise<BacktestSettings> {
  return getJson<BacktestSettings>('/analyses/backtest/settings', signal);
}

/**
 * Update the replay rules. Stored results are untouched — they are keyed by the
 * rule set they were computed under, so the next sweep writes a new set
 * alongside them rather than overwriting.
 */
export function updateBacktestSettings(
  patch: BacktestSettingsPatch,
): Promise<BacktestSettings> {
  return patchJson<BacktestSettings>('/analyses/backtest/settings', patch);
}

/** Rule sets that have stored results, for the preset picker. */
export function listBacktestCombos(signal?: AbortSignal): Promise<BacktestCombo[]> {
  return getJson<BacktestCombo[]>('/analyses/backtest/combos', signal);
}
